// מסמכי לקוחות — every stored file that belongs to a client, in one place, organised the
// way the records actually nest: client → retainer → job.
//
// FOUR FILE TABLES, ONE SCREEN. A retainer's signed agreements (045), a job's supplier
// contracts (046), the scans of invoices (051) and the clippings of publications
// (030/034) are separate tables with separate owners and separate upload paths, and
// until now the only way to see one was to be on the record that owns it. The question
// this page answers is the one none of those screens can: "where is that document",
// asked without already knowing which client, engagement or job it was filed under.
//
// DISPLAY ONLY, AND THAT IS THE DESIGN. Nothing here uploads, edits, archives or
// deletes. The server domain mounts one GET and has no write path at all; every action
// stays where the document lives — a contract on its retainer or its job, a scan on its
// charge in the money screen, a clipping on its publication. So each fold links to the
// record it stands for: the moment you notice a document is missing, the screen that can
// fix it is one click away.
//
// IT BUILDS NO FILE HANDLING. Every row carries the `kind` the server stamped on it, and
// FileActions — reused exactly as it is — asks THAT domain's existing download-url
// endpoint for a short-lived signed URL. No new route, no second copy of the open/alert
// logic, no third copy of the inline-safe allowlist (the server decides `viewable`).
//
// OFFICE DOCUMENTS ARE NOT HERE, in any form. A business document belongs to no client —
// that is what it IS, expressed in a table with no client column and an R2 branch that
// is a sibling of `clients/` rather than a null inside it. They have their own screen,
// which can actually change them. This page does not concern itself with them.
//
// THREE FOLDS DEEP, one more than the client card's work tree, because the client level
// is new: there it is the page you are already on, here it is the top of the index.
import { useMemo, useState } from 'react';
import useAsync from '../../hooks/useAsync';
import useCollapsedGroups from '../../hooks/useCollapsedGroups';
import { clientDocumentsApi } from '../../api';
import he from '../../i18n/he';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import Button from '../../components/Button';
import SearchBox from '../../components/SearchBox';
import EmptyState from '../../components/EmptyState';
import ClientNode from './ClientNode';
import filterTree from './filter';

// A client, a retainer and a job can all be id 3, so the fold map namespaces them — and
// the map has its own storage key, or a client here and a client on the board would fold
// each other.
const cKey = (id) => `c:${id}`;
const rKey = (id) => `r:${id}`;
const jKey = (id) => `j:${id}`;
const STORAGE_KEY = 'pressbooks.clientdocs.collapsed';

// Clients closed, retainers open, jobs closed. Opening a client should show its
// engagements with their contracts already on screen, and the work beneath them one more
// click away — the same shape the client card settled on, with the client level added on
// top. Every client open at once would be the whole archive in a single scroll.
const CLIENT_DEFAULT = true;   // collapsed
const RETAINER_DEFAULT = false;
const JOB_DEFAULT = true;

export default function ClientDocuments() {
  // The archive is a separate VIEW, not a filter: it re-reads, because which rows are
  // archived is the server's question. The two are never mixed — an archived document is
  // out of sight by definition.
  const [archived, setArchived] = useState(false);
  const [query, setQuery] = useState('');
  const { isCollapsed, setCollapsed, setAll } = useCollapsedGroups(STORAGE_KEY);

  const { data, loading, error } = useAsync(
    () => clientDocumentsApi.tree({ archived }), [archived]
  );

  const clients = useMemo(() => data?.clients || [], [data]);
  const shown = useMemo(() => filterTree(clients, query), [clients, query]);

  // WHILE A SEARCH IS RUNNING, EVERY FOLD IS OPEN — and nothing is written to the fold
  // map to do it. A filtered tree whose matches are behind three closed folds has
  // answered the question and hidden the answer; and overriding the display rather than
  // the stored state means clearing the box puts every fold back exactly as the partner
  // left it.
  const searching = query.trim().length > 0;
  const isOpen = (key, fallback) => searching || !isCollapsed(key, fallback);
  const toggle = (key, fallback) => setCollapsed(key, !isCollapsed(key, fallback));

  // Expand-all / collapse-all reaches all three levels at once. One that stopped at the
  // clients would leave every document still hidden, which is the opposite of what the
  // button is for. Built from the UNFILTERED tree so the choice survives a search.
  const allKeys = useMemo(() => {
    const keys = [];
    for (const client of clients) {
      keys.push(cKey(client.id));
      for (const job of client.jobs) keys.push(jKey(job.id));
      for (const retainer of client.retainers) {
        keys.push(rKey(retainer.id));
        for (const job of retainer.jobs) keys.push(jKey(job.id));
      }
    }
    return keys;
  }, [clients]);

  const emptyText = () => {
    if (searching) return he.clientDocuments.emptySearch;
    return archived ? he.clientDocuments.emptyArchive : he.clientDocuments.empty;
  };

  return (
    <>
      <PageHeader title={he.clientDocuments.title} spread>
        <SearchBox value={query} onChange={setQuery} placeholder={he.clientDocuments.search} />
        <div className="row">
          <Button small variant="subtle" onClick={() => setArchived((a) => !a)}>
            {archived ? he.clientDocuments.showLive : he.clientDocuments.showArchive}
          </Button>
          <Button small variant="subtle" onClick={() => setAll(allKeys, false)}>
            {he.board.expandAll}
          </Button>
          <Button small variant="subtle" onClick={() => setAll(allKeys, true)}>
            {he.board.collapseAll}
          </Button>
        </div>
      </PageHeader>

      <AsyncBoundary loading={loading} error={error}>
        {/* `docs-tree` marks the extra depth: this tree folds client → retainer → job,
            where the client card's starts at the retainer. See clientwork.css. */}
        <div className="card work-tree docs-tree">
          {shown.length > 0
            ? shown.map((client) => (
              <ClientNode
                key={client.id}
                client={client}
                archived={archived}
                open={isOpen(cKey(client.id), CLIENT_DEFAULT)}
                onToggle={() => toggle(cKey(client.id), CLIENT_DEFAULT)}
                isRetainerOpen={(id) => isOpen(rKey(id), RETAINER_DEFAULT)}
                onToggleRetainer={(id) => toggle(rKey(id), RETAINER_DEFAULT)}
                isJobOpen={(id) => isOpen(jKey(id), JOB_DEFAULT)}
                onToggleJob={(id) => toggle(jKey(id), JOB_DEFAULT)}
              />
            ))
            : <EmptyState text={emptyText()} />}
        </div>
      </AsyncBoundary>
    </>
  );
}
